import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../store';
import {
  getAgencyConfig,
  updateAgencyConfig,
  type AgencyConfigPayload,
} from '../services/agencyConfig';
import type { AgencyConfigUpdate } from '../services/api/types.gen';

type Options<V> = {
  /** False skips the read, e.g. a section hidden at this agency. */
  enabled: boolean;
  initial: V;
  /** The section's values from the stored config. Module level, so stable. */
  fromConfig: (cfg: AgencyConfigPayload) => V;
  /** Only what moved: the same row carries settings other screens own. */
  diff: (values: V, saved: V) => AgencyConfigUpdate;
  /** The values to keep once a save lands. */
  afterSave?: (values: V) => V;
  successMessage: string;
  errorMessage: string;
};

/**
 * Load, edit and save one Admin section's slice of the agency config.
 *
 * A failed read tells us nothing about the agency's settings, so the section
 * must not offer to write them: `loadFailed` keeps Edit off. Cancel restores
 * the last server values, and a save with nothing moved writes nothing.
 */
export function useAgencyConfigSection<V>({
  enabled,
  initial,
  fromConfig,
  diff,
  afterSave = values => values,
  successMessage,
  errorMessage,
}: Options<V>) {
  const { agencyId, showSuccess, showError } = useStore(
    useShallow(state => ({
      agencyId: state.user?.agency?.id,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [values, setValues] = useState<V>(initial);
  const [savedValues, setSavedValues] = useState<V>(initial);

  // fromConfig must be stable (module level), or every render reloads.
  useEffect(() => {
    if (!agencyId || !enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const cfg = await getAgencyConfig();
        if (cancelled) return;
        const loaded = fromConfig(cfg);
        setValues(loaded);
        setSavedValues(loaded);
      } catch (err) {
        console.error('Failed to load agency config:', err);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [agencyId, enabled, fromConfig]);

  const cancel = () => {
    setValues(savedValues);
    setIsEditing(false);
  };

  const save = async () => {
    const body = diff(values, savedValues);
    if (Object.keys(body).length === 0) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await updateAgencyConfig(body);
      const saved = afterSave(values);
      setValues(saved);
      setSavedValues(saved);
      setIsEditing(false);
      showSuccess(successMessage);
    } catch (err) {
      console.error('Failed to update agency config:', err);
      setValues(savedValues);
      showError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    values,
    setValues,
    isEditing,
    startEditing: () => setIsEditing(true),
    cancel,
    save,
    isSaving,
    isLoading,
    loadFailed,
  };
}
